import dayjs from "dayjs";

const MINUTES_PER_DAY = 24 * 60;

const pad = (value) => String(value).padStart(2, "0");

export const parseTime = (value) => {
	if (value === null || value === undefined) {
		return null;
	}

	if (dayjs.isDayjs(value)) {
		const hours = value.hour();
		const minutes = value.minute();
		return {
			hours,
			minutes,
			totalMinutes: hours * 60 + minutes,
			source: "dayjs",
			input: value
		};
	}

	if (typeof value === "string") {
		const trimmed = value.trim();
		if (!trimmed) {
			return null;
		}
		const match = trimmed.match(/^(-?\d+)(?::(\d{1,2}))?$/);
		if (match) {
			const hours = Number(match[1]);
			const minutes = Number(match[2] ?? "0");
			if (Number.isNaN(hours) || Number.isNaN(minutes)) {
				return null;
			}
			return {
				hours,
				minutes,
				totalMinutes: hours * 60 + minutes,
				source: "string",
				input: value
			};
		}

		const date = new Date(trimmed);
		if (!Number.isNaN(date.getTime())) {
			const hours = date.getUTCHours();
			const minutes = date.getUTCMinutes();
			return {
				hours,
				minutes,
				totalMinutes: hours * 60 + minutes,
				source: "iso-string",
				input: value
			};
		}

		return null;
	}

	if (typeof value === "number" && Number.isFinite(value)) {
		const hours = Math.trunc(value / 60);
		const minutes = Math.abs(Math.trunc(value)) % 60;
		return {
			hours,
			minutes,
			totalMinutes: value,
			source: "number",
			input: value
		};
	}

	if (typeof value === "object") {
		if (value?.toDate) {
			return parseTime(dayjs(value.toDate()));
		}
		if (typeof value.seconds === "number") {
			return parseTime(dayjs.unix(value.seconds));
		}
		if (typeof value.hour === "number") {
			const hours = value.hour;
			const minutes = Number(value.minute ?? value.minutes ?? 0);
			if (Number.isNaN(minutes)) {
				return null;
			}
			return {
				hours,
				minutes,
				totalMinutes: hours * 60 + minutes,
				source: "object",
				input: value
			};
		}
	}

	return null;
};

const formatComponents = (hours, minutes) => `${pad(hours)}:${pad(minutes)}`;

export const normalizeStoredTimeString = (value) => {
	const parsed = parseTime(value);
	if (!parsed) {
		return null;
	}
	return formatComponents(parsed.hours, parsed.minutes);
};

export const toPickerValue = (value) => {
	const parsed = parseTime(value);
	if (!parsed) {
		return null;
	}

	const base = dayjs().startOf("day");
	const normalizedHours = ((parsed.hours % 24) + 24) % 24;

	return base
		.hour(normalizedHours)
		.minute(parsed.minutes)
		.second(0)
		.millisecond(0);
};

export const formatTimeForStorage = (value, reference) => {
	const parsed = parseTime(value);
	if (!parsed) {
		return null;
	}

	let totalMinutes = parsed.totalMinutes;
	const referenceParsed = parseTime(reference);
	const isExplicitOverflow = parsed.hours >= 24;

	if (referenceParsed && !isExplicitOverflow) {
		const normalizedReference = ((referenceParsed.totalMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
		const normalizedValue = ((parsed.totalMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
		if (normalizedValue <= normalizedReference) {
			totalMinutes += MINUTES_PER_DAY;
		}
	}

	const hours = Math.floor(totalMinutes / 60);
	const minutes = totalMinutes % 60;

	return formatComponents(hours, minutes);
};

export const getTotalMinutes = (value) => {
	const parsed = parseTime(value);
	return parsed ? parsed.totalMinutes : null;
};

export const isTimeRangeComplete = (start, end) => {
	const startMinutes = getTotalMinutes(start);
	const endMinutes = getTotalMinutes(end);

	if (startMinutes === null || endMinutes === null) {
		return false;
	}

	return endMinutes > startMinutes;
};

export const MINUTES_IN_DAY = MINUTES_PER_DAY;

const firstDefined = (...candidates) =>
	candidates.find((candidate) => candidate !== undefined && candidate !== null);

export const normalizeDayHours = (value) => {
	if (!value || typeof value !== "object") {
		return {
			isOpen: false,
			openTime: null,
			closeTime: null
		};
	}

	const isOpen = Boolean(value.isOpen ?? value.is_open ?? false);
	const openRaw = firstDefined(
		value.openTime,
		value.open_time
	);
	let openTime = normalizeStoredTimeString(openRaw);

	if (!openTime) {
		const fallbackOpen = firstDefined(
			value.openTime2,
			value.open_time2,
			value.open_time_2
		);
		openTime = normalizeStoredTimeString(fallbackOpen);
	}

	const closeRaw = firstDefined(value.closeTime, value.close_time);
	const referenceForClose = openTime ?? openRaw ?? null;
	let closeTime = closeRaw
		? formatTimeForStorage(closeRaw, referenceForClose)
		: null;

	const secondaryCloseRaw = firstDefined(
		value.closeTime2,
		value.close_time2,
		value.close_time_2
	);
	let secondaryCloseTime = secondaryCloseRaw
		? formatTimeForStorage(secondaryCloseRaw, referenceForClose)
		: null;

	const primaryMinutes = getTotalMinutes(closeTime);
	const secondaryMinutes = getTotalMinutes(secondaryCloseTime);

	if (
		secondaryMinutes !== null &&
		(primaryMinutes === null || secondaryMinutes > primaryMinutes)
	) {
		closeTime = secondaryCloseTime;
	}

	return {
		isOpen,
		openTime,
		closeTime
	};
};

const convertDisplayToStored = (value) => {
	if (value === null || value === undefined) {
		return null;
	}

	if (typeof value === "string") {
		const trimmed = value.trim();
		if (!trimmed) {
			return null;
		}

		const lower = trimmed.toLowerCase();
		const ampmMatch = lower.match(/^([0-9]{1,2})(?::([0-9]{2}))?\s*(am|pm)$/);
		if (ampmMatch) {
			let hours = Number(ampmMatch[1]);
			const minutes = Number(ampmMatch[2] ?? "0");
			const meridiem = ampmMatch[3];

			if (Number.isNaN(hours) || Number.isNaN(minutes)) {
				return null;
			}

			if (meridiem === "pm" && hours !== 12) {
				hours += 12;
			}
			if (meridiem === "am" && hours === 12) {
				hours = 0;
			}

			return `${pad(hours)}:${pad(minutes)}`;
		}

		const condensedMatch = lower.match(/^([0-9]{1,2})(am|pm)$/);
		if (condensedMatch) {
			return convertDisplayToStored(`${condensedMatch[1]} ${condensedMatch[2]}`);
		}
	}

	const normalized = normalizeStoredTimeString(value);
	return normalized;
};

export const calculateDisplayTime = (value) => {
	const parsed = parseTime(value);
	if (!parsed) {
		return "";
	}

	const normalizedMinutes = ((parsed.totalMinutes % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
	const hours24 = Math.floor(normalizedMinutes / 60);
	const minutes = normalizedMinutes % 60;
	const period = hours24 >= 12 ? "PM" : "AM";
	const hours12 = hours24 % 12 || 12;

	return `${hours12}:${pad(minutes)} ${period}`;
};

export const calculateFormattedTime = (displayValue, reference) => {
	const storedCandidate = convertDisplayToStored(displayValue);
	if (!storedCandidate) {
		return null;
	}

	return formatTimeForStorage(storedCandidate, reference);
};
